/**
 * 설정 편집 창 위쪽 — 현장 통합서버가 지금 실제로 읽고 있는 파일과 저장소 파일 비교.
 *
 * 현장 파일은 10분 주기 점검(site-health live)이 읽어 둔 것이다. 다르면 현장 원문을 펼쳐 보고
 * 편집칸에 그대로 넣을 수 있다 — 현장에서 손으로 고친 값을 저장소로 되가져올 때.
 * 컨테이너 환경변수가 파일 키를 덮으면 그 키는 파일을 고쳐도 현장 값이 안 바뀐다고 알린다.
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Stack, TextField } from '@mui/material';

import type { SiteConfigContent } from '../../../services/SiteConfigService';
import { SiteHealthService, type SiteLiveConfigFile } from '../../../services/SiteHealthService';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import { LIVE_CONFIG_TEXT, compareLiveConfig } from './siteConfigFiles';

interface Props {
  projectId: number;
  /** 방재실(현장 점검 group) — 하나면 '' */
  room: string;
  app: string;
  name: string;
  live?: SiteLiveConfigFile;
  /** 저장소에서 읽은 파일 — 아직 못 읽었으면 null */
  repo: Pick<SiteConfigContent, 'exists' | 'sha'> | null;
  disabled: boolean;
  onUse: (content: string) => void;
}

const LiveConfigPanel: React.FC<Props> = ({ projectId, room, app, name, live, repo, disabled, onUse }) => {
  const { type } = useDashboardTokens();
  const [content, setContent] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!repo) return null;
  const compare = compareLiveConfig(repo, live);
  const where = live?.path ? ` (${live.path})` : '';

  const show = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (content === null) {
      setLoading(true);
      setError(null);
      try {
        setContent((await SiteHealthService.liveConfig(projectId, room, app, name)).content);
      } catch (e) {
        setError(e instanceof Error ? e.message : '현장 파일을 읽지 못했습니다');
        return;
      } finally {
        setLoading(false);
      }
    }
    setOpen(true);
  };

  const overrides = live?.env_overrides ?? [];
  return (
    <Stack spacing={1} data-testid="live-config-panel" data-live={compare}>
      {compare === 'unknown' ? (
        <Alert severity="info">현장 실제 파일을 아직 읽지 못했습니다 — 위 [현장 다시 읽기] 뒤에 다시 열어 보세요.</Alert>
      ) : compare === 'same' ? (
        <Alert severity="success">
          {LIVE_CONFIG_TEXT.same} — 현장이 지금 이 내용을 읽고 있습니다{where}.
        </Alert>
      ) : compare === 'different' ? (
        <Alert severity="warning">
          {LIVE_CONFIG_TEXT.different} — 현장{where}은 다른 내용을 읽고 있습니다. 저장하면 다음 배포 때 이 내용으로 바뀝니다.
          {/* 버튼은 문구 아래 줄 — 오른쪽 action 칸은 폰 폭에서 글자가 꺾인다 */}
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1 }}>
            <Button variant="outlined" color="inherit" size="small" onClick={() => void show()} disabled={loading} sx={{ minHeight: 44, whiteSpace: 'nowrap' }}>
              {loading ? '읽는 중…' : open ? '현장 내용 숨기기' : '현장 내용 보기'}
            </Button>
            {open && content !== null && (
              <Button variant="outlined" color="inherit" size="small" onClick={() => onUse(content)} disabled={disabled} sx={{ minHeight: 44, whiteSpace: 'nowrap' }}>
                편집칸에 넣기
              </Button>
            )}
          </Stack>
        </Alert>
      ) : (
        <Alert severity="info">
          {LIVE_CONFIG_TEXT[compare]}
          {where}
          {compare === 'site_missing' && ' — 현장 서비스는 이 파일 없이 돌고 있습니다.'}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {open && content !== null && (
        <Box>
          <TextField
            value={content}
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            label="현장 실제 내용 (읽기 전용)"
            InputProps={{ readOnly: true }}
            inputProps={{
              'data-testid': 'live-config-content',
              spellCheck: false,
              style: { fontFamily: type.mono.family, fontSize: 16, lineHeight: 1.5, whiteSpace: 'pre' },
            }}
          />
        </Box>
      )}
      {overrides.length > 0 && (
        <Alert severity="info" data-testid="live-config-overrides">
          현장 컨테이너 환경변수가 파일 값보다 우선합니다: <b>{overrides.join(', ')}</b> — 이 키는 파일을 고쳐도 현장 값이
          바뀌지 않습니다(현장 compose 설정을 확인하세요).
        </Alert>
      )}
    </Stack>
  );
};

export default LiveConfigPanel;
